import React, { useEffect, useRef, useState } from 'react'

import { BaseView, BaseText } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'

interface BubbleProps {
  cx: number
  cy: number
  selectedValue: number
  selectedDateFormatted: string
  containerWidth: number
}

export const Bubble: React.FC<BubbleProps> = ({
  cx,
  cy,
  selectedValue,
  selectedDateFormatted,
  containerWidth,
}) => {
  const theme = useTheme()
  const bubbleRef = useRef<HTMLDivElement>(null)
  const [bubbleWidth, setBubbleWidth] = useState(0)

  useEffect(() => {
    if (bubbleRef.current) {
      setBubbleWidth(bubbleRef.current.offsetWidth)
    }
  }, [selectedValue, selectedDateFormatted])

  const formattedValue = `$${(Math.floor(selectedValue * 100) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`

  // Adjust position to keep bubble within container bounds
  let adjustedCx = cx - bubbleWidth / 2
  let indicatorPosition: 'center' | 'flex-start' | 'flex-end' = 'center'

  if (adjustedCx <= 0) {
    adjustedCx = 0
    indicatorPosition = 'flex-start'
  } else if (adjustedCx + bubbleWidth >= containerWidth) {
    adjustedCx = containerWidth - bubbleWidth
    indicatorPosition = 'flex-end'
  }

  // Triangle rotation and offset based on position
  let triangleDeg = '0deg'
  let triangleOffsetX = '0px'
  let triangleOffsetY = '0px'

  if (adjustedCx <= 0) {
    triangleDeg = '32deg'
    triangleOffsetX = '2px'
    triangleOffsetY = '-6px'
  } else if (adjustedCx + bubbleWidth >= containerWidth) {
    triangleDeg = '-32deg'
    triangleOffsetX = '-2px'
    triangleOffsetY = '-6px'
  }

  return (
    <div
      ref={bubbleRef}
      style={{
        position: 'absolute',
        left: `${adjustedCx}px`,
        top: `${cy - 85}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: indicatorPosition,
        transition: 'all 0.15s cubic-bezier(0.4, 0.0, 0.2, 1)',
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    >
      <BaseView
        style={{
          backgroundColor: theme.colors.background.cardItemRoot,
          borderRadius: '20px',
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingTop: '8px',
          paddingBottom: '8px',
          gap: '4px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <BaseText
          fontSize={16}
          fontFamily="semibold"
          color="label.default"
          textAlign="center"
          style={{
            whiteSpace: 'nowrap',
            paddingTop: 0,
            paddingBottom: 0,
          }}
        >
          {formattedValue}
        </BaseText>
        <BaseText
          fontSize={12}
          fontFamily="regular"
          color="label.default"
          textAlign="center"
          style={{
            whiteSpace: 'nowrap',
            paddingTop: 0,
            paddingBottom: 0,
          }}
        >
          {selectedDateFormatted}
        </BaseText>
      </BaseView>
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderTop: `10px solid ${theme.colors.background.cardItemRoot}`,
          marginTop: '-1px',
          transform: `rotate(${triangleDeg}) translate(${triangleOffsetX}, ${triangleOffsetY})`,
        }}
      />
    </div>
  )
}
